import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { api } from '../utils/api';
import BookingModal from '../components/BookingModal';

const CATEGORIES = [
  { key: 'general',             label: 'Eventos generales',        icon: '🏕',  description: 'Eventos puntuales y actividades en la aldea' },
  { key: 'campamento_verano',   label: 'Campamentos de verano',    icon: '☀️',  description: 'Campamentos de verano con pernocta' },
  { key: 'campamento_familiar', label: 'Campamentos familiares',   icon: '👨‍👩‍👧', description: 'Experiencias para disfrutar en familia' },
  { key: 'school',              label: 'Viajes de fin de curso',   icon: '🏫',  description: 'Inscripciones para centros escolares' },
];

export default function Events() {
  const { token } = useAuth();
  const [view, setView] = useState('hub'); // 'hub' | 'general' | 'campamento_verano' | 'campamento_familiar' | 'school'
  const [normalEvents, setNormalEvents] = useState([]);
  const [schoolEvents, setSchoolEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selected, setSelected] = useState(null);
  const [success, setSuccess] = useState('');
  const [passwordPrompt, setPasswordPrompt] = useState(null);
  const [eventPassword, setEventPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [verifyingPassword, setVerifyingPassword] = useState(false);

  // Validar la contraseña contra el servidor ANTES de abrir el formulario;
  // si es incorrecta, el evento no se abre.
  const tryEventPassword = async () => {
    if (!eventPassword || verifyingPassword) return;
    setVerifyingPassword(true);
    setPasswordError('');
    try {
      await api.verifyEventPassword(token, passwordPrompt.id, eventPassword);
      setSelected({ ...passwordPrompt, _eventPassword: eventPassword });
      setPasswordPrompt(null);
    } catch (e) {
      setPasswordError(e.message || 'Contraseña del evento incorrecta.');
    } finally {
      setVerifyingPassword(false);
    }
  };

  useEffect(() => {
    setLoading(true);
    Promise.all([
      api.getPublicEvents(token),
      api.request('GET', '/api/events?category=school', null, token),
    ])
      .then(([normal, school]) => {
        setNormalEvents(normal.events || []);
        setSchoolEvents(school.events || []);
      })
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [token]);

  const handleSuccess = () => {
    setSelected(null);
    setSuccess('¡Inscripción realizada! Recibirás un correo de confirmación en breve.');
    setTimeout(() => setSuccess(''), 5000);
  };

  // Contador de eventos por categoria
  const countByCategory = (key) => {
    if (key === 'school') return schoolEvents.length;
    return normalEvents.filter(e => (e.subcategory || 'general') === key).length;
  };

  // Eventos visibles segun la vista seleccionada
  const events = view === 'hub' ? []
    : view === 'school' ? schoolEvents
    : normalEvents.filter(e => (e.subcategory || 'general') === view);

  const activeCategory = CATEGORIES.find(c => c.key === view);

  if (loading) return (
    <div className="loading-screen" style={{ minHeight: 'auto', padding: '3rem' }}>
      <div className="spinner spinner-lg" />
    </div>
  );

  // ── HUB: menu principal con 4 categorias ────────────────────────────
  if (view === 'hub') {
    return (
      <div className="main-content page-enter">
        <div style={{ marginBottom: '1.8rem' }}>
          <div className="section-label">La Aldea</div>
          <h1>¿Que te gustaria hacer?</h1>
          <p style={{ color: 'var(--mid)', fontStyle: 'italic', fontSize: '.95rem', marginTop: '.2rem' }}>
            Elige el tipo de experiencia que quieres reservar
          </p>
        </div>

        {error && <div className="alert alert-error"><span>⚠</span> {error}</div>}
        {success && <div className="alert alert-success"><span>🌿</span> {success}</div>}

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(260px,1fr))', gap: '1rem' }}>
          {CATEGORIES.map(cat => {
            const count = countByCategory(cat.key);
            const disabled = count === 0;
            return (
              <button
                key={cat.key}
                className="card"
                onClick={() => !disabled && setView(cat.key)}
                disabled={disabled}
                style={{
                  textAlign: 'left',
                  padding: '1.5rem',
                  display: 'flex', flexDirection: 'column', gap: '.6rem',
                  cursor: disabled ? 'not-allowed' : 'pointer',
                  opacity: disabled ? .5 : 1,
                  border: '1px solid rgba(122,171,138,.25)',
                  transition: 'transform .15s ease, box-shadow .15s ease, border-color .15s ease',
                  background: 'inherit',
                  fontFamily: 'inherit',
                }}
                onMouseEnter={e => { if (!disabled) { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.borderColor = 'var(--sage)'; } }}
                onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.borderColor = 'rgba(122,171,138,.25)'; }}
              >
                <div style={{ fontSize: '2.4rem', lineHeight: 1 }}>{cat.icon}</div>
                <div style={{ fontFamily: "'Fraunces',serif", fontSize: '1.1rem', fontWeight: 700, color: 'var(--sage)' }}>
                  {cat.label}
                </div>
                <div style={{ fontSize: '.82rem', color: 'var(--mid)', fontStyle: 'italic', lineHeight: 1.4 }}>
                  {cat.description}
                </div>
                <div style={{ marginTop: 'auto', paddingTop: '.4rem', fontSize: '.75rem', color: disabled ? 'var(--mid)' : 'var(--sage)', fontWeight: 600 }}>
                  {disabled
                    ? 'Sin disponibilidad'
                    : `${count} ${count === 1 ? 'disponible' : 'disponibles'} →`
                  }
                </div>
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  // ── VISTA DE CATEGORIA: listado de eventos filtrado ─────────────────
  return (
    <div className="main-content page-enter">
      <div style={{ marginBottom: '1.8rem' }}>
        <button className="btn btn-ghost btn-sm" onClick={() => setView('hub')} style={{ marginBottom: '.8rem' }}>
          ← Volver
        </button>
        <div className="section-label">La Aldea</div>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: '1rem' }}>
          <div>
            <h1>{activeCategory?.icon} {activeCategory?.label}</h1>
            <p style={{ color: 'var(--mid)', fontStyle: 'italic', fontSize: '.95rem', marginTop: '.2rem' }}>
              {activeCategory?.description}
            </p>
          </div>
        </div>
      </div>

      {error && <div className="alert alert-error"><span>⚠</span> {error}</div>}
      {success && <div className="alert alert-success"><span>🌿</span> {success}</div>}

      {/* Aviso de anonimato para colegios */}
      {view === 'school' && (
        <div className="alert alert-info" style={{ marginBottom: '1.2rem' }}>
          <span>🔒</span> Los nombres de los centros se muestran como IDs anónimos para proteger la privacidad.
        </div>
      )}

      {events.length === 0 && !loading && !error && (
        <div className="card" style={{ textAlign: 'center', padding: '3rem', color: 'var(--mid)', fontStyle: 'italic' }}>
          No hay {view === 'school' ? 'inscripciones escolares' : 'eventos'} disponibles en este momento.
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(300px,1fr))', gap: '1rem' }}>
        {events.map(event => {
          const occupied = event.currentCount + (event.externalCount || 0);
          const full = occupied >= event.maxCapacity;
          const pct  = Math.min(100, Math.round((occupied / event.maxCapacity) * 100));
          const free = Math.max(0, event.maxCapacity - occupied);
          return (
            <div className="card" key={event.id} style={{ display: 'flex', flexDirection: 'column', gap: '.65rem', opacity: full ? .6 : 1 }}>
              <div>
                <div style={{ fontFamily: "'Fraunces',serif", fontSize: '.6rem', fontWeight: 700, letterSpacing: '.15em', color: 'var(--sage)', textTransform: 'uppercase' }}>
                  {event.sinFechas || !event.date
                    ? 'Tú eliges tus fechas de entrada y salida'
                    : event.fechasFlexibles && event.dateEnd
                      ? `Del ${new Date(event.date).toLocaleDateString('es-ES', { day: 'numeric', month: 'long' })} al ${new Date(event.dateEnd).toLocaleDateString('es-ES', { day: 'numeric', month: 'long', year: 'numeric' })} · tú eliges tus fechas`
                      : new Date(event.date).toLocaleDateString('es-ES', { weekday: 'short', day: 'numeric', month: 'long', year: 'numeric' })}
                </div>
                <div style={{ fontFamily: "'Fraunces',serif", fontSize: '1rem', fontWeight: 700, color: 'var(--sage)', margin: '.2rem 0' }}>
                  {event.name}
                </div>
                {event.location && <div style={{ fontSize: '.8rem', color: 'var(--sage)' }}>📍 {event.location}</div>}
                {(event.checkInTime || event.checkOutTime) && (
                  <div style={{ fontSize: '.8rem', color: 'var(--sage)' }}>
                    🕓 {[event.checkInTime && `Entrada ${event.checkInTime}`, event.checkOutTime && `Salida ${event.checkOutTime}`].filter(Boolean).join(' · ')}
                  </div>
                )}
              </div>
              {event.description && (
                <p style={{ fontSize: '.85rem', color: 'var(--mid)', fontStyle: 'italic', lineHeight: 1.55 }}>
                  {event.description}
                </p>
              )}
              {event.requiresPayment && (
                <div style={{ fontSize: '.82rem', color: 'var(--sage)', marginTop: '.3rem' }}>
                  {event.useAgePricing ? (
                    <span>👶 Gratis · 🧒 {event.agePricing?.child?.price} € · 🧑 {event.agePricing?.adult?.price} €</span>
                  ) : (
                    <span>💶 {event.pricePerPerson} € / persona</span>
                  )}
                </div>
              )}
              {/* Campamentos de verano: no mostrar plazas/ocupación a las familias.
                  Moderadores y admins las ven en sus propias pantallas (/moderador, /admin/eventos). */}
              {event.subcategory !== 'campamento_verano' && (
                <div className="cap-wrap">
                  <div className="cap-track">
                    <div className="cap-fill" style={{ width: `${pct}%`, background: full ? 'var(--danger)' : undefined }} />
                  </div>
                  <div className="cap-info">
                    <span>{occupied} / {event.maxCapacity}</span>
                    {full
                      ? <span style={{ color: 'var(--danger)' }}>Completo</span>
                      : <span style={{ color: free < 10 ? 'var(--warn)' : undefined }}>{free} plazas libres</span>
                    }
                  </div>
                </div>
              )}
              <div style={{ marginTop: 'auto', paddingTop: '.5rem' }}>
                <div style={{ display: 'flex', gap: '.4rem', flexWrap: 'wrap', marginBottom: '.8rem' }}>
                  <span className={`badge ${full ? 'badge-danger' : 'badge-green'}`}>
                    {full ? 'Completo' : 'Activo'}
                  </span>
                  <span className={`badge ${event.registrationType === 'individual' ? 'badge-indiv' : 'badge-group'}`}>
                    {event.registrationType === 'individual' ? '👤 Individual' : '👨‍👩‍👧 Grupal'}
                  </span>
                </div>
                <button
                  className="btn btn-primary btn-full"
                  onClick={() => event.hasPassword ? (setPasswordPrompt(event), setEventPassword(''), setPasswordError('')) : setSelected(event)}
                  disabled={full}
                  style={full ? { opacity: .5, cursor: 'not-allowed' } : {}}
                >
                  {full ? 'Sin plazas disponibles' : 'Reservar plaza'}
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {passwordPrompt && (
        <div className="modal-overlay" onClick={() => setPasswordPrompt(null)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 420 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <h2 style={{ margin: 0, fontSize: '1.05rem' }}>🔒 Evento protegido</h2>
              <button className="btn btn-ghost" onClick={() => setPasswordPrompt(null)} style={{ fontSize: '1.2rem' }}>✕</button>
            </div>
            <p style={{ color: 'var(--mid)', fontSize: '.9rem', marginBottom: '1rem' }}>
              Este evento requiere una contraseña para inscribirse. Solicítala al organizador.
            </p>
            {passwordError && <div className="alert alert-error" style={{ marginBottom: '.8rem' }}><span>⚠</span> {passwordError}</div>}
            <div className="form-group">
              <label className="form-label">Contraseña</label>
              <input className="form-input" type="password" value={eventPassword}
                onChange={e => { setEventPassword(e.target.value); setPasswordError(''); }}
                placeholder="Introduce la contraseña"
                onKeyDown={e => { if (e.key === 'Enter') tryEventPassword(); }}
                autoFocus />
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '.5rem', marginTop: '1rem' }}>
              <button className="btn btn-ghost" onClick={() => setPasswordPrompt(null)}>Cancelar</button>
              <button className="btn btn-primary" disabled={!eventPassword || verifyingPassword}
                onClick={tryEventPassword}>
                {verifyingPassword ? <><span className="spinner" /> Comprobando...</> : 'Acceder'}
              </button>
            </div>
          </div>
        </div>
      )}

      {selected && <BookingModal event={selected} onClose={() => setSelected(null)} onSuccess={handleSuccess} />}
    </div>
  );
}
